import React from 'react'
// libs
import { createFilter } from 'react-select'
import { change, FieldArray, formValueSelector } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray'
// components
import { ReduxEntitySelect, ReduxSelect } from 'components/FormElements'
// constants
import { Forms } from 'constants/forms'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// graphql
import { RISK_TEMPLATE_SELECT_QUERY } from 'graphql/risk/queries'
// types
import { FieldType } from 'types/graphql'
import { IOption } from 'components/FormElements/Select'
// views
import { AddButton, CloseIcon } from 'components/Modals/LookupFilterModal/components/Form/components/Rules/views'
import { FieldsWrapper, MappingContainer, MappingWrapper } from './views'
// utils
import castArray from 'lodash/castArray'
import { required } from 'utils/validators'
import { riskTemplatesProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

const selector = formValueSelector(Forms.PROPERTY_EDIT_FORM)

const defaultFilter = createFilter<IOption>()

const excludedFieldTypes = [
  FieldType.Label,
  FieldType.Table,
  FieldType.Section,
  FieldType.RecordLinks,
  FieldType.ParentRelatedForms,
  FieldType.DocumentOrLink,
  FieldType.RiskMatrix
]

interface IMapping {
  templateId?: IOption
}

interface MappingProps {
  field: string
  index: number
  remove: () => void
  isMultiple: boolean
}

const Mapping: React.FC<MappingProps> = ({ field, index, remove, isMultiple }) => {
  const dispatch = useDispatch()

  const riskTemplates = useSelector(riskTemplatesSelector)

  const templateId = useSelector((state) => selector(state, `${field}.templateId`)) as IOption | undefined
  // Array for Parent Related Forms (isMultiple), a single option or null for RecordLinks —
  // castArray normalizes either shape without caring which one it got.
  const fieldIds = castArray(useSelector((state) => selector(state, `${field}.fieldIds`)) || []) as IOption[]
  const mappings = (useSelector((state) => selector(state, 'settings.fieldMappings')) ?? []) as IMapping[]

  // the same parent form can be mapped only once
  const mappedTemplateIds = mappings
    .filter((_, idx) => idx !== index)
    .map((mapping) => mapping?.templateId?.value)
    .filter(Boolean)

  const selectedFieldIds = fieldIds.map(({ value }) => value)

  // properties belong to the selected form, so they make no sense once another form is picked
  const onTemplateChange = (option: IOption) => {
    if (selectedFieldIds.length && option?.value !== templateId?.value) {
      dispatch(change(Forms.PROPERTY_EDIT_FORM, `${field}.fieldIds`, null))
    }
  }

  const fieldOptions = riskTemplates
    .find(({ id }) => id === templateId?.value)
    ?.fields.filter((field) => !excludedFieldTypes.includes(field.fieldType) && !selectedFieldIds.includes(field.id))
    .map((field) => ({ label: field.label, value: field.id }))

  return (
    <MappingWrapper>
      <FieldsWrapper>
        <ReduxEntitySelect
          label='Parent Form'
          menuPlacement='top'
          validate={required}
          menuPortalTarget={null}
          name={`${field}.templateId`}
          query={RISK_TEMPLATE_SELECT_QUERY}
          onSelectChange={onTemplateChange}
          filterOption={(option, input) => !mappedTemplateIds.includes(option.value) && defaultFilter(option, input)}
          {...riskTemplatesProps}
        />
        <ReduxSelect
          isMultiple={isMultiple}
          label='Properties'
          validate={required}
          options={fieldOptions ?? []}
          menuPlacement='top'
          menuPortalTarget={null}
          name={`${field}.fieldIds`}
        />
      </FieldsWrapper>
      <CloseIcon onClick={remove} />
    </MappingWrapper>
  )
}

const FieldMappings: React.FC<WrappedFieldArrayProps & { isMultiple: boolean }> = ({ fields, isMultiple }) => {
  const add = () => fields.push({})
  const remove = (idx: number) => () => fields.remove(idx)

  return (
    <MappingContainer>
      Fields Mapping
      {fields.map((field, idx) => (
        <Mapping key={field} field={field} index={idx} remove={remove(idx)} isMultiple={isMultiple} />
      ))}
      <AddButton onClick={add}>+ Add Mapping</AddButton>
    </MappingContainer>
  )
}

interface FieldMappingsSettingsProps {
  // RecordLinks: one field per mapped form. Parent Related Forms: several.
  isMultiple: boolean
}

const FieldMappingsSettings: React.FC<FieldMappingsSettingsProps> = ({ isMultiple }) => (
  <FieldArray<any> name='settings.fieldMappings' component={FieldMappings} isMultiple={isMultiple} />
)

export default FieldMappingsSettings
